<template>
  <Dialog
    :visible="visible"
    @update:visible="(val) => emit('update:visible', val)"
    :header="isEditing ? 'Edit Task' : 'New Task'"
    :style="{ width: '500px' }"
    :modal="true"
  >
    <div class="p-fluid">
      <IftaLabel class="w-[95%]">
        <Dropdown
          id="status"
          v-model="taskForm.status"
          :options="statusOptions"
          optionLabel="label"
          optionValue="value"
          class="w-full"
        />
        <label for="status">Status</label>
      </IftaLabel>

      <IftaLabel class="w-[95%] my-2">
        <InputText id="title" v-model="taskForm.title" required class="w-full" />
        <label for="title">Title</label>
      </IftaLabel>

      <IftaLabel class="w-[95%] my-2">
        <Textarea id="description" v-model="taskForm.description" rows="3" class="w-full" />
        <label for="description">Description</label>
      </IftaLabel>

      <IftaLabel class="w-[95%] my-2">
        <Dropdown
          id="assignedTo"
          v-model="taskForm.assignedTo"
          :options="userOptions"
          optionLabel="name"
          optionValue="id"
          placeholder="Select user"
          class="w-full"
        />
        <label for="assignedTo">Assigned To</label>
      </IftaLabel>

      <div class="field w-[95%] my-2">
        <InputGroup class="w-full">
          <InputText
            :value="taskForm.dueAt ? formatDate(taskForm.dueAt) : ''"
            placeholder="Select due date"
            readonly
            @click="showCalendar = !showCalendar"
            class="w-full"
          />
          <InputGroupAddon>
            <Button
              icon="pi pi-calendar"
              severity="secondary"
              variant="text"
              @click="showCalendar = !showCalendar"
            />
          </InputGroupAddon>
        </InputGroup>
        <div class="flex justify-center mt-2">
          <Calendar
            v-if="showCalendar"
            id="dueAt"
            v-model="taskForm.dueAt"
            :attributes="calendarAttributes"
            :initial-page="
              taskForm.dueAt
                ? {
                    month: new Date(taskForm.dueAt).getMonth() + 1,
                    year: new Date(taskForm.dueAt).getFullYear(),
                  }
                : undefined
            "
            transparent
            borderless
            color="green"
            style="width: 400px; margin: 0 auto"
            @dayclick="
              (day) => {
                taskForm.dueAt = day.date
                showCalendar = false
              }
            "
          />
        </div>
      </div>

      <div v-if="isEditing && taskForm.id" class="mt-4 pt-4 border-t border-gray-200">
        <div class="text-xs text-gray-500 text-center">Task ID: {{ taskForm.id }}</div>
      </div>
    </div>
    <template #footer>
      <Button label="Cancel" icon="pi pi-times" class="p-button-text" @click="closeDialog" />
      <Button label="Save" icon="pi pi-check" @click="saveTask" />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useTaskStore } from '@/stores/taskStore'
import { formatDate } from '@/utils/formatters'
import type { Task } from '@/types/Task'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import Dropdown from 'primevue/dropdown'
import Button from 'primevue/button'
import { Calendar } from 'v-calendar'
import 'v-calendar/style.css'
import InputGroup from 'primevue/inputgroup'
import InputGroupAddon from 'primevue/inputgroupaddon'
import IftaLabel from 'primevue/iftalabel'

const props = defineProps<{
  visible: boolean
  isEditing: boolean
  task?: Task
}>()

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'save', task: Partial<Task>): void
}>()

const taskStore = useTaskStore()
const showCalendar = ref(false)

// Status options
const statusOptions = [
  { label: 'To Do', value: 'todo' },
  { label: 'In Progress', value: 'doing' },
  { label: 'Done', value: 'done' },
  { label: 'Cancelled', value: 'cancelled' },
]

// User options (placeholder until user store is implemented)
const userOptions = computed(() => {
  return [
    { id: null, name: 'All Users' },
    // Add more users when user store is implemented
  ]
})

// Form state
const taskForm = ref<Partial<Task>>({
  title: '',
  description: '',
  status: 'todo',
  dueAt: undefined,
  assignedTo: undefined,
})

// Reset form to initial state
const resetForm = () => {
  taskForm.value = {
    title: '',
    description: '',
    status: 'todo',
    dueAt: undefined,
    assignedTo: undefined,
  }
}

// Watch for task prop changes
watch(
  () => props.task,
  (newTask) => {
    if (newTask) {
      taskForm.value = { ...newTask }
    } else {
      resetForm()
    }
  },
  { immediate: true },
)

// Watch for dialog visibility
watch(
  () => props.visible,
  (isVisible) => {
    if (!isVisible) {
      resetForm()
    }
  },
)

// Task grouping and color logic
const tasksByDueDate = computed(() => {
  const grouped = new Map<string, number>()
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  console.log('All tasks:', taskStore.items)

  taskStore.items.forEach((task) => {
    if (task.dueAt) {
      console.log('Processing task:', {
        title: task.title,
        dueAt: task.dueAt,
        dueAtType: typeof task.dueAt,
        dueAtString: task.dueAt.toString(),
      })
      const date = typeof task.dueAt === 'string' ? new Date(task.dueAt) : task.dueAt
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const dateStr = `${year}-${month}-${day}`
      console.log('Formatted date:', dateStr)

      if (date >= today) {
        grouped.set(dateStr, (grouped.get(dateStr) || 0) + 1)
      }
    }
  })

  console.log('Final grouped map:', Object.fromEntries(grouped))
  return grouped
})

// Get color based on task count
const getTaskColor = (count: number): string => {
  if (count >= 25) return 'red'
  if (count >= 15) return 'orange'
  if (count >= 5) return 'yellow'
  return 'green'
}

// Calendar attributes computation
const calendarAttributes = computed(() => {
  const attributes: Array<{
    highlight?: {
      color: string
      fillMode: 'outline'
    }
    bar?: {
      color: string
    }
    dates: Date[]
    popover: {
      label: string
      visibility: 'hover'
    }
  }> = []

  // Add bars for all tasks
  tasksByDueDate.value.forEach((count, dateStr) => {
    // Parse the date string back into a Date object
    const [year, month, day] = dateStr.split('-').map(Number)
    const date = new Date(year, month - 1, day)
    date.setHours(12, 0, 0, 0) // Set to noon to avoid timezone issues

    attributes.push({
      bar: {
        color: getTaskColor(count),
      },
      dates: [date],
      popover: {
        label: `${count} task${count === 1 ? '' : 's'} due`,
        visibility: 'hover',
      },
    })
  })

  // Add highlight for today
  const today = new Date()
  today.setHours(12, 0, 0, 0)
  attributes.push({
    highlight: {
      color: 'blue',
      fillMode: 'outline',
    },
    dates: [today],
    popover: {
      label: 'Today',
      visibility: 'hover',
    },
  })

  // Add highlight for the selected date if it exists
  if (taskForm.value.dueAt) {
    const selectedDate = new Date(taskForm.value.dueAt)
    selectedDate.setHours(12, 0, 0, 0)
    attributes.push({
      highlight: {
        color: 'green',
        fillMode: 'outline',
      },
      dates: [selectedDate],
      popover: {
        label: 'Selected due date',
        visibility: 'hover',
      },
    })
  }

  return attributes
})

// Dialog methods
const closeDialog = () => {
  emit('update:visible', false)
}

const saveTask = () => {
  emit('save', taskForm.value)
}
</script>

<style>
.vc-week {
  margin: 12px 0;
}
</style>
